// Finance build — shared range-param parsing for the Income Overview surface
// (the overview page + the per-loan drill both carry the same window in the
// URL). Defaults are derived at PARSE time (current month), never in a
// post-mount effect, so the first render already requests the right window.

import type {
  IncomeOverviewParams,
  IncomeOverviewRange,
} from "@/api/portfolioAdmin/incomeOverviewApi";

export const INCOME_RANGE_OPTIONS: ReadonlyArray<{
  value: IncomeOverviewRange;
  label: string;
}> = [
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
  { value: "lifetime", label: "Lifetime" },
  { value: "custom", label: "Custom range" },
];

const RANGE_VALUES = new Set<string>(INCOME_RANGE_OPTIONS.map((o) => o.value));

/** Parse a raw `?range=` value, defaulting to the current month. */
export function parseRange(raw: string | null): IncomeOverviewRange {
  return raw && RANGE_VALUES.has(raw) ? (raw as IncomeOverviewRange) : "month";
}

/**
 * Build the query params from the URL search params. Only `range` is sent for
 * the named windows; `from`/`to` ride along only for a custom range (and only
 * when present), since the backend resolves every named window itself.
 */
export function readIncomeOverviewParams(
  params: URLSearchParams,
): IncomeOverviewParams {
  const range = parseRange(params.get("range"));
  if (range !== "custom") return { range };
  const from = params.get("from")?.trim() || undefined;
  const to = params.get("to")?.trim() || undefined;
  return { range, from, to };
}

/** Carry the window params from one URL onto another route's link. */
export function rangeSearch(params: IncomeOverviewParams): string {
  const search = new URLSearchParams();
  search.set("range", params.range);
  if (params.range === "custom") {
    if (params.from) search.set("from", params.from);
    if (params.to) search.set("to", params.to);
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}
